import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import ConfirmarAcaoModal from './ConfirmarAcaoModal';
import Data from './Data';
import api from '../services/api';
import notify from '../services/notify';
import { PERFIS_RSVP, PERFIS_EVENTOS } from '../util/malhaAereo';

const DIA = 'DD/MM/YYYY';

// Departamentos que fecham o evento, pelo perfil: a mesma lista de Evento::FECHAMENTO_DEPARTAMENTOS. Aqui ela
// só decide o que aparece, no Painel de Eventos e no rodapé do cadastro; quem fecha é conferido no back pelo
// cargo real.
export const DEPARTAMENTOS_FECHAMENTO = {
    backoffice: { aba: 'Fechados pelo Backoffice', perfis: ['backoffice'] },
    rsvp: { aba: 'Fechados pelo RSVP', perfis: PERFIS_RSVP },
    eventos: { aba: 'Fechados por Eventos', perfis: PERFIS_EVENTOS },
};
export const departamentoDoPerfil = (perfil) => Object.keys(DEPARTAMENTOS_FECHAMENTO)
    .find(departamento => DEPARTAMENTOS_FECHAMENTO[departamento].perfis.includes(perfil)) ?? null;

// A situação de um departamento pelo último registro dele: fechado (com a data informada no fechamento),
// reaberto (com o dia em que desfez) ou aberto, quando nunca fechou.
function situacaoDoDepartamento(item) {
    if (item.fechado) {
        return { pill: 'is-ok', texto: 'Fechado', data: moment(item.fechado_em).format(DIA) };
    }
    if (item.usuario) {
        return { pill: 'is-warn', texto: 'Reaberto', data: moment(item.data).format(DIA) };
    }
    return { pill: 'is-muted', texto: 'Aberto', data: '—' };
}

/**
 * O modal do Fechamento, no Painel de Eventos e no rodapé do cadastro do evento: o último registro de cada
 * departamento (Backoffice, RSVP e Eventos), para cada um saber dos outros, e o Fechar ou o Desfazer do
 * departamento de quem está logado. O evento fecha quando os três fecharam. Usuário, departamento e hora saem
 * do back (`PUT evento/fechar`); daqui vai só a data do fechamento, que começa em hoje e não pode ser futura.
 * `onAplicado()`: quem abriu o modal relê o que precisar.
 */
export default function FechamentoEventoModal({ evento, departamento, onFechar, onAplicado }) {
    const token = localStorage.getItem('token');
    const [resumo, setResumo] = useState(null);
    const [data, setData] = useState(moment().format(DIA));
    const [gravando, setGravando] = useState(false);

    useEffect(() => {
        api({ method: 'get', action: '/evento/fechamento/' + evento.id, token })
            .then(res => setResumo(res.data.data ?? null))
            .catch(err => notify.apiError(err, 'Erro ao carregar o fechamento'));
    }, [evento.id, token]);

    const desfazer = !!resumo?.[departamento]?.fechado;
    const dataEscolhida = moment(data, DIA, true);
    const dataInvalida = !dataEscolhida.isValid() || dataEscolhida.isAfter(moment(), 'day');
    const outrosFechados = !!resumo && Object.keys(resumo).filter(d => d !== departamento).every(d => resumo[d].fechado);

    const gravar = async () => {
        setGravando(true);
        try {
            const res = await api({
                method: 'put',
                action: '/evento/fechar/' + evento.id,
                token,
                data: desfazer ? { status: 'reaberto' } : { status: 'fechado', dataFechamento: dataEscolhida.format('YYYY-MM-DD') },
            });
            toast.success(res.data?.data?.mensagem || (desfazer ? 'Fechamento desfeito' : 'Fechamento registrado'));
            onAplicado();
        } catch (err) {
            notify.apiError(err, desfazer ? 'Erro ao desfazer o fechamento' : 'Erro ao fechar o evento');
        } finally {
            setGravando(false);
        }
    };

    const aviso = !resumo ? null
        : desfazer && evento.situacao === 'fechado' ? 'Desfazer reabre o evento, que volta para pendente ou ativo.'
        : !desfazer && outrosFechados ? 'Os outros dois departamentos já fecharam: com este fechamento, o evento fecha.'
        : null;

    return (
        <ConfirmarAcaoModal aberto titulo="Fechamento do evento" subtitulo={evento.nome}
            rotulo={desfazer ? 'Desfazer fechamento' : 'Fechar'} tom={desfazer ? 'danger' : 'ok'} icone={desfazer ? 'alteracao' : 'ok'}
            aviso={aviso} processando={gravando} confirmarPreso={!resumo || (!desfazer && dataInvalida)}
            onConfirmar={gravar} onFechar={onFechar}>
            <div className="fin-pp__tblwrap" style={{ marginBottom: 16 }}>
                <table className="fin-pp__tbl">
                    <thead>
                        <tr><th>Departamento</th><th>Situação</th><th>Data</th><th>Por</th></tr>
                    </thead>
                    <tbody>
                        {!resumo ? (
                            <tr><td colSpan={4} className="fin-pp__empty">Carregando...</td></tr>
                        ) : Object.keys(resumo).map(d => {
                            const situacao = situacaoDoDepartamento(resumo[d]);
                            return (
                                <tr key={d}>
                                    <td className="fin-pp__cell-strong">
                                        {resumo[d].departamento}
                                        {d === departamento && <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }}>Seu</span>}
                                    </td>
                                    <td><span className={'fin-pp__pill ' + situacao.pill}>{situacao.texto}</span></td>
                                    <td className="fin-pp__cell-muted">{situacao.data}</td>
                                    <td className="fin-pp__cell-muted">{resumo[d].nome || '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {resumo && !desfazer && (
                <>
                    <Data label="Data do fechamento" value={data} onChange={setData} maxDate={new Date()} />
                    {dataInvalida && (
                        <div style={{ color: 'var(--fin-dg)', fontSize: 12, marginTop: 6 }}>Informe uma data até hoje.</div>
                    )}
                </>
            )}
        </ConfirmarAcaoModal>
    );
}
